import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Trash2, Lock, CalendarDays } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/molecules/confirm-dialog";
import { useMesures } from "@/core/hooks/useMesures";
import { usePreviousMesure } from "@/core/hooks/usePreviousMesure";
import { useViewedCampagne } from "@/core/hooks/useViewedCampagne";
import { useLiveQueryWithStatus, useLiveQuery } from "@/core/hooks/useLiveQuery";
import { offlineDb, deletePinCascade } from "@/core/offline/db";
import { deleteWithOfflineFallback } from "@/core/offline/delete-with-fallback";
import { MesureForm } from "@/modules/corrosion/components/mesure-form";
import { PhotoSection } from "@/components/organisms/photo-section";

export default function PinMesurePage() {
  const params = useParams<{ siteId: string; zoneId: string; pinId: string }>();
  const navigate = useNavigate();
  const [showConfirm, setShowConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | undefined>();

  const { data: pin, isLoading: pinLoading } = useLiveQueryWithStatus(
    () => offlineDb.pins.get(params.pinId!),
    [params.pinId]
  );

  // Campagne visualisée : soit celle demandée via ?campagneId=X, soit l'active
  const { viewedCampagne, activeCampagne, isViewingHistorical, campagnes } = useViewedCampagne(params.siteId!);
  const campagneId = viewedCampagne?.id;

  // Zone pour récupérer l'état verrouillé
  const zone = useLiveQuery(
    () => params.zoneId ? offlineDb.zones.get(params.zoneId) : undefined,
    [params.zoneId]
  );

  // Read-only si :
  // - on visualise une campagne historique (clôturée ou autre que l'active)
  // - OU le site est clôturé (pas de campagne active + campagnes existantes)
  // - OU la zone est verrouillée
  const siteIsClosed = !activeCampagne && campagnes.length > 0;
  const zoneLocked = zone?.locked ?? false;
  const isReadOnly = isViewingHistorical || siteIsClosed || zoneLocked;

  const { mesure, isLoading: mesureLoading, createMesure, updateFields, deleteMesure } = useMesures(
    params.pinId!,
    campagneId
  );

  // Mesure de la campagne précédente (pour rappel discret sous min/max dans le form)
  const previousMesureInfo = usePreviousMesure(params.pinId, campagneId);

  // Wrap updateFields pour bloquer les modifs en lecture seule
  const safeUpdateFields: typeof updateFields = isReadOnly
    ? async () => { /* no-op : lecture seule */ }
    : updateFields;

  // Créer automatiquement une mesure si le pin n'en a pas pour la campagne active.
  // Attendre que la query Dexie ait résolu ET que la campagne active soit disponible.
  const creatingRef = useRef(false);
  useEffect(() => {
    if (pin && campagneId && !mesureLoading && mesure === null && !creatingRef.current) {
      creatingRef.current = true;
      createMesure().catch((err) => {
        console.error("[PinMesure] createMesure:", err);
        creatingRef.current = false;
      });
    }
  }, [pin, campagneId, mesureLoading, mesure, createMesure]);

  // Scroll lock + Escape quand le modal est ouvert
  useEffect(() => {
    if (!showConfirm) return;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setShowConfirm(false);
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      document.removeEventListener("keydown", onKey);
    };
  }, [showConfirm]);

  /** Supprime la mesure + le pin (avec sync serveur), puis retour au plan.
   *  Passe par deleteWithOfflineFallback : 404 serveur toléré (pin pas encore
   *  synchronisé) et bascule offline sur panne réseau/timeout. */
  async function handleDelete() {
    setDeleting(true);
    setDeleteError(undefined);
    try {
      if (mesure) {
        await deleteMesure(mesure.id);
      }
      await deleteWithOfflineFallback("pin", params.pinId!, `/api/pins/${params.pinId}`, () =>
        deletePinCascade(params.pinId!)
      );
      navigate(-1);
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : "Erreur lors de la suppression");
      setDeleting(false);
    }
  }

  if (pinLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <p className="text-muted-foreground">Chargement...</p>
      </div>
    );
  }

  if (!pin) {
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-4">
        <p className="text-muted-foreground">Point introuvable.</p>
        <Button variant="outline" onClick={() => navigate(-1)}>
          Retour
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen bg-background">
      {/* Header */}
      <header className="flex items-center gap-3 px-4 py-3 border-b bg-background sticky top-0 z-10">
        <Button
          variant="ghost"
          size="icon-sm"
          onClick={() => navigate(-1)}
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>

        <div className="flex-1 min-w-0">
          <h1 className="text-base font-semibold truncate">
            Mesure n°{pin.number}
          </h1>
          {viewedCampagne && (
            <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
              <CalendarDays className="size-3" />
              Campagne du {new Date(viewedCampagne.date).toLocaleDateString("fr-FR", {
                day: "numeric",
                month: "long",
                year: "numeric",
              })}
            </p>
          )}
        </div>

        {!isReadOnly && (
          <Button
            variant="ghost"
            size="icon-sm"
            className="tap-target rounded-lg ring-1 ring-destructive/15 text-destructive hover:bg-destructive/10"
            onClick={() => setShowConfirm(true)}
          >
            <Trash2 className="size-4" />
          </Button>
        )}
      </header>

      {/* Bandeau lecture seule */}
      {isReadOnly && (
        <div className="flex items-center gap-2 px-4 py-2 bg-muted text-xs text-muted-foreground border-b">
          <Lock className="size-3.5" />
          <span className="flex-1 min-w-0">
            {isViewingHistorical
              ? "Consultation d'une campagne clôturée : fiche figée."
              : siteIsClosed
              ? "Site clôturé : fiche en lecture seule. Démarre une nouvelle campagne pour modifier."
              : "Zone verrouillée : fiche en lecture seule. Déverrouille la zone pour modifier."}
          </span>
          {isViewingHistorical && activeCampagne && (
            <button
              className="text-primary underline text-xs shrink-0"
              onClick={() => navigate(`/sites/${params.siteId}/zones/${params.zoneId}/pins/${params.pinId}`)}
            >
              Campagne actuelle
            </button>
          )}
        </div>
      )}

      {/* Photos + Formulaire */}
      <main className="flex-1 overflow-y-auto px-4 pt-4">
        {mesure ? (
          <div className={isReadOnly ? "opacity-75 pointer-events-none" : ""}>
            <PhotoSection mesureId={mesure.id} mesureNumero={mesure.numero} />
            <div className="mt-6">
              <MesureForm
                mesure={mesure}
                onUpdateFields={safeUpdateFields}
                previousMesure={previousMesureInfo?.mesure}
                previousCampagneDate={
                  previousMesureInfo ? new Date(previousMesureInfo.campagne.date) : undefined
                }
              />
            </div>
          </div>
        ) : (
          <p className="text-muted-foreground text-center mt-8">
            Création de la fiche en cours...
          </p>
        )}
      </main>

      {/* Dialog de confirmation suppression */}
      <ConfirmDialog
        open={showConfirm}
        title="Supprimer cette fiche ?"
        description={`La fiche de mesure n°${pin.number} et le point sur le plan seront définitivement supprimés. Cette action est irréversible.`}
        loading={deleting}
        error={deleteError}
        onConfirm={handleDelete}
        onCancel={() => {
          setShowConfirm(false);
          setDeleteError(undefined);
        }}
      />
    </div>
  );
}
